<template>
    <div class="price-wrapper">
        <div>
            <div class="base-price-wrapper">
                <div v-if="tileBasePrice" class="d-flex flex-wrap align-items-center">
                    <span
                        v-msh-html="tileBasePrice + '/mois'"
                        class="p my-0 fw-bold text-decoration-line-through"
                        :class="isBlack ? 'text-white' : 'text-dark'"
                    />
                    <div
                        v-if="tileTotalDiscountsAndOdrAmount"
                        class="ob1-tag ob1-tag-small ob1-tag-primary"
                        style="margin-left: 10px;"
                    >
                        <span v-msh-html="tileTotalDiscountsAndOdrAmount + '/mois'" class="p my-0" />
                        <span v-if="clientTagLabel">{{ clientTagLabel }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <div class="final-price-wrapper">
                <Price
                    :price="tileFinalPrice"
                    :initial-price="tileFinalPricePermanent"
                    :price-details="price.priceDetails ?? undefined"
                    :duration="tileDuration"
                    :is-cents-format="false"
                    :is-compact="true"
                    :is-inverse="isBlack"
                    :show-from="price.showFrom"
                    :show-by-month="price.showByMonth"
                    :show-promo-duration="price.showPromoDuration"
                    :show-end-of-promotion-price="price.showEndOfPromotionPrice"
                    :show-children-promotion-price="price.showChildrenPromotionPrice"
                    :cashback-type="cashbackType"
                />
                <div v-if="priceDetailsPopin && priceDetailsPopin.linkLabel">
                    <Lien
                        :label="priceDetailsPopin.linkLabel"
                        :is-inverse="isBlack"
                        :is-button="true"
                        :data-test-id="'btn-price-detail-' + offerIdSeo"
                        :modal-id="isEmbeddedDetails ? '' : 'price-details-popin-' + offerIdSeo"
                        :tms-category="trackingCategory"
                        :tms-action="priceDetailsPopin.linkLabel"
                        :tms-label="offerName"
                        @click="showDetails"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import Price from "../ob1/Price.vue";
import Lien from "../ob1/Lien.vue";
import {PrixDTO} from "../../../../../../generated/js/OrderingDtos";
import {PriceDetailsCashbackType, PriceDetailsDiscountType, PriceDetailsPopinDto} from "./models/PriceDetailsPopinDto";
import {ClientTag} from "./models/ClientTag";
import {formatPriceToEuros} from "../../utils/Number";

export default defineComponent({
    name: "PriceWithDetails",
    components: {
        Price,
        Lien
    },
    props: {
        price: {
            type: Object as PropType<PrixDTO>,
            required: true
        },
        priceDetailsPopin: {
            type: Object as PropType<PriceDetailsPopinDto | undefined>,
            required: false,
            default: undefined
        },
        offerIdSeo: {
            type: String,
            required: true
        },
        offerName: {
            type: String,
            required: true
        },
        trackingCategory: {
            type: String,
            required: true
        },
        isBlack: {
            type: Boolean,
            default: false
        },
        isEmbeddedDetails: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        tileBasePrice(): string | null {
            return this.priceDetailsPopin?.basePrice
                ? formatPriceToEuros(this.priceDetailsPopin.basePrice, false)
                : null;
        },
        tileTotalDiscountsAndOdrAmount(): string | null {
            return this.priceDetailsPopin?.totalDiscountsAndCashbacksAmount
                ? formatPriceToEuros(this.priceDetailsPopin.totalDiscountsAndCashbacksAmount, false)
                : null;
        },
        tileFinalPrice(): number {
            return this.priceDetailsPopin?.finalPrice ?? this.price.price;
        },
        tileFinalPricePermanent(): number | undefined {
            return this.priceDetailsPopin?.finalPricePermanent ?? this.price.initialPrice ?? undefined;
        },
        tileDuration(): number {
            return this.priceDetailsPopin?.finalPriceDuration ?? this.price.duration;
        },
        cashbackType(): PriceDetailsCashbackType | undefined {
            if (this.priceDetailsPopin) {
                if (this.priceDetailsPopin.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.CHEAT_CODE
                )) {
                    return PriceDetailsCashbackType.CHEAT_CODE;
                } else if (this.priceDetailsPopin.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.SAFER_PHONE
                )) {
                    return PriceDetailsCashbackType.SAFER_PHONE;
                } else if (this.priceDetailsPopin.cashbacks.some(
                    (cashback) => cashback.cashbackType === PriceDetailsCashbackType.OTHER
                )) {
                    return PriceDetailsCashbackType.OTHER;
                }
            }
            return undefined;
        },
        discountType(): ClientTag | undefined {
            if (this.priceDetailsPopin) {
                if (this.priceDetailsPopin.discounts.some(
                    (discount) => discount.type === PriceDetailsDiscountType.MOBILE_CLIENT
                )) {
                    return ClientTag.MOBILE_CLIENT;
                } else if (this.priceDetailsPopin.discounts.some(
                    (discount) => discount.type === PriceDetailsDiscountType.INTERNET_CLIENT
                )) {
                    return ClientTag.INTERNET_CLIENT;
                }
            }
            return undefined;
        },
        clientTagLabel(): string | undefined {
            if (this.discountType === ClientTag.MOBILE_CLIENT) {
                return "Client mobile";
            } else if (this.discountType === ClientTag.INTERNET_CLIENT) {
                return "Client internet";
            }
            return undefined;
        },
    },
    methods: {
        showDetails(): void {
            if (this.isEmbeddedDetails) {
                this.$emit('show-details');
            }
        }
    }
});
</script>
